import { useCallback, useMemo, useState } from 'react';
import { useFieldArray, type UseFormReturn, useWatch } from 'react-hook-form';

import { MAX_SESSIONS } from '../constants';

import { mapTimesToSessions } from './utils';

function generateKey() {
  return Math.random().toString(36).substring(2, 15);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useSessionFormManagement({ form }: { form: UseFormReturn<any> }) {
  const [keys] = useState<Map<number, string>>(new Map());

  const formSessions: {
    startedAt?: Date;
    endedAt?: Date;
  }[] = useWatch({
    control: form.control,
    name: 'sessions',
    disabled: !form,
  });

  const { append, remove } = useFieldArray({
    control: form.control,
    name: 'sessions',
    rules: {
      maxLength: MAX_SESSIONS,
    },
  });

  const { formState } = form;

  const { sessions, totalDuration } = useMemo(() => {
    return mapTimesToSessions(formSessions, keys, formState);
  }, [formSessions, formState, keys]);

  const createNewSession = useCallback(
    (amount: number) => {
      if (formSessions.length >= MAX_SESSIONS) {
        return;
      }

      const validatedAmount = Math.min(Math.max(amount, 1), MAX_SESSIONS - formSessions.length);

      for (let i = 0; i < validatedAmount; i++) {
        append({
          startedAt: undefined,
          endedAt: undefined,
        });
        const key = generateKey();
        const order = formSessions.length + 1 + i;
        keys.set(order, key);
      }
    },
    [append, formSessions.length, keys],
  );

  const addSession = useCallback(() => {
    if (formSessions.length <= 0) {
      //REMARK: create extra session if no session exists to ensure at least two sessions are present
      createNewSession(2);
      return;
    }

    createNewSession(1);
  }, [createNewSession, formSessions.length]);

  function removeSession(targetOrder: number) {
    remove(targetOrder - 1);
    keys.delete(targetOrder);
    for (let i = targetOrder; i <= formSessions.length; i++) {
      const key = keys.get(i);
      if (key) {
        keys.set(i - 1, key);
        keys.delete(i);
      }
    }
  }

  return {
    sessions,
    totalDuration,
    addSession,
    removeSession,
  };
}
